import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package } from 'lucide-react';
import ProductForm from '../../components/forms/ProductForm';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const ProductEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (company?.id && id) {
      loadProduct();
    }
  }, [company, id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      console.log('Loading product for edit with ID:', id, 'for company:', company.id);
      
      const foundProduct = await dataHelpers.getProductById(company.id, id);
      
      if (!foundProduct) {
        console.log('Product not found with ID:', id);
        toast.error('Produto não encontrado');
        navigate('/dashboard/products');
        return;
      }
      
      console.log('Product loaded for edit:', foundProduct);
      setProduct(foundProduct);
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Erro ao carregar produto');
      navigate('/dashboard/products');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (productData) => {
    try {
      setSaving(true);
      
      console.log('Updating product with data:', productData);
      
      await dataHelpers.updateProduct(product.id, productData);
      
      toast.success('Produto atualizado com sucesso!');
      
      // Redirecionar para a página de detalhes
      navigate(`/dashboard/products/${product.id}`, {
        state: { message: 'Produto atualizado com sucesso!' }
      });
      
    } catch (error) {
      console.error('Error updating product:', error);
      
      let errorMessage = 'Erro ao atualizar produto';
      
      if (error.message?.includes('duplicate')) {
        errorMessage = 'Já existe um produto com este código de barras';
      } else if (error.message?.includes('barcode')) {
        errorMessage = 'Código de barras já está em uso';
      } else if (error.message?.includes('company_id')) {
        errorMessage = 'Erro de autorização. Verifique se você tem permissão para editar produtos';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/dashboard/products/${product.id}`);
  };

  if (loading) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="large" />
        </div>
      
    );
  }

  if (!product) {
    return (
      
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Produto não encontrado</h2>
          <p className="text-gray-600 mb-6">O produto que você está procurando não existe ou foi removido.</p>
          <Button onClick={() => navigate('/dashboard/products')}>
            Voltar para Produtos
          </Button>
        </div>
      
    );
  }

  return (
    
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate(`/dashboard/products/${product.id}`)}
              className="flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Editar Produto</h1>
              <p className="text-gray-600">
                Editando: {product.name}
              </p>
            </div>
          </div>
        </div>

        {/* Informações da empresa */}
        {company && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <Package className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-blue-800">
                Editando produto para: <strong>{company.name}</strong>
              </span>
            </div>
          </div>
        )}

        {/* Formulário */}
        <ProductForm
          initialData={product}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={saving}
        />
      </div>
    
  );
};

export default ProductEditPage;
